interface Permission {
  id: string
  label: string
  isSelected: boolean
  weight: number
  visible: boolean
  parent_permission_id: string | null
  rolesAllowed: Permission[]
}

export function formatData(allPermissions: any[], editRolePermissionsList: any[]): Permission[] {
  const permissionsList: Permission[] = []
  allPermissions.forEach((permission: any) => {
    if (!permission.name.startsWith('TAB')) {
      const isSelected = editRolePermissionsList.some((value: any) => value.name === permission.name)
      const value: Permission = {
        id: permission.name,
        label: permission.display_name,
        isSelected: Boolean(isSelected),
        weight: permission.weight,
        visible: permission.visible,
        parent_permission_id: permission.parent_permission_id,
        rolesAllowed: []
      }
      permissionsList.push(value)
    }
  })
  return permissionsList
}

export function buildTree(rootPermissions: Permission[], allPermissions: Permission[]): Permission[] {
  const buildSubTree = (permission: Permission): Permission => {
    const children = allPermissions
      .filter(p => p.parent_permission_id === permission.id && p.visible)
      .sort((a, b) => a.weight - b.weight)
    permission.rolesAllowed = children.map(buildSubTree)
    return permission
  }

  return rootPermissions
    .map(buildSubTree)
    .filter(p => p.visible)
    .sort((a, b) => a.weight - b.weight)
}

// Helper to toggle all child permissions recursively
const toggleChildPermissions = (checked: boolean, children: Permission[]): Permission[] =>
  children.map(child => ({
    ...child,
    isSelected: checked,
    rolesAllowed: toggleChildPermissions(checked, child.rolesAllowed)
  }))

const unSelectAll = (targetId: string, permissions: Permission[]): Permission[] => {
  return permissions.map(permission => {
    const parent_permission_id = permission.parent_permission_id
    if (permission.id === targetId) {
      return {
        ...permission,
        isSelected: false,
        rolesAllowed: toggleChildPermissions(false, permission.rolesAllowed)
      }
    } else if (permission.rolesAllowed.some(role => role.id === targetId)) {
      return {
        ...permission,
        isSelected: false,
        rolesAllowed: permission.rolesAllowed.length > 0 ? toggleChildPermissions(false, permission.rolesAllowed) : []
      }
    }
    return permission
  })
}

const checkForView = (event: any, targetId: string, permissions: Permission[]): boolean => {
  //Recursion is not handled
  return permissions.some(permission => {
    if (permission.id === targetId) {
      if (permission.label === 'View') {
        return true
      }
      return false
    }
    // Recursively check in nested rolesAllowed
    return checkForView(event, targetId, permission.rolesAllowed || [])
  })
}

const hasChildWithId = (targetId: string, rolesAllowed: Permission[]): boolean => {
  return rolesAllowed.some(role => role.id === targetId || hasChildWithId(targetId, role.rolesAllowed))
}

// Recursive function to handle updates based on new rules
export const updatePermissionTree = (event: any, targetId: string, permissions: Permission[]): Permission[] => {
  if (!event.target.checked) {
    const isView = checkForView(event, targetId, permissions)
    if (isView) {
      return unSelectAll(targetId, permissions)
    }
  }

  return permissions.map(permission => {
    if (permission.id === targetId) {
      // Toggle non-View permissions and propagate
      return {
        ...permission,
        isSelected: event.target.checked,
        rolesAllowed:
          permission.rolesAllowed.length > 0
            ? toggleChildPermissions(event.target.checked, permission.rolesAllowed)
            : []
      }
    }

    if (hasChildWithId(targetId, permission.rolesAllowed)) {
      const updatedRolesAllowed = permission.rolesAllowed.map(
        role =>
          role.id === targetId || (role.label === 'View' && event.target.checked)
            ? {
                ...role,
                isSelected: event.target.checked,
                rolesAllowed:
                  role.rolesAllowed.length > 0 ? toggleChildPermissions(event.target.checked, role.rolesAllowed) : []
              }
            : updatePermissionTree(event, targetId, [role])[0] // Recursive check for deeper levels
      )
      return {
        ...permission,
        rolesAllowed: updatedRolesAllowed,
        isSelected: event.target.checked ? true : updatedRolesAllowed.some(child => child.isSelected)
      }
    }

    return permission
  })
}

export const selectAllPermissions = (event: any, permissions: Permission[]): Permission[] => {
  return permissions.map(permission => {
    return {
      ...permission,
      isSelected: event.target.checked,
      rolesAllowed:
        permission.rolesAllowed.length > 0 ? toggleChildPermissions(event.target.checked, permission.rolesAllowed) : []
    }
  })
}

export const getSelectedPermissions = (permissions: Permission[]): string[] => {
  return permissions.reduce<string[]>((acc, permission) => {
    if (permission.isSelected) {
      acc.push(permission.id)
    }

    // Check if rolesAllowed is a valid array
    if (Array.isArray(permission.rolesAllowed) && permission.rolesAllowed.length > 0) {
      const nestedSelected = getSelectedPermissions(permission.rolesAllowed)
      acc.push(...nestedSelected)
    }

    return acc
  }, [])
}
